/**
 * Token管理页面
 * 用户查看余额、充值记录、用量统计
 * 管理员可以为用户充值、查看系统配置
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  PlusIcon,
  ArrowPathIcon,
  CalendarIcon,
  XMarkIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { tokenApi } from '../services/api';
import Toast from '../components/Toast';

interface TokenManagementProps {
  currentUser: {
    id: number;
    name: string;
    role: string;
  };
}

interface SystemConfigItemProps {
  config: {
    key: string;
    value: string;
    description: string;
  };
  onSave: (newValue: string) => Promise<boolean>;
}

// 系统配置项组件 - 支持编辑/显示切换
const SystemConfigItem: React.FC<SystemConfigItemProps> = ({ config, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(config.value);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const success = await onSave(editValue);
    setSaving(false);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(config.value);
    setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{config.key}</p>
        <p className="text-xs text-gray-500 mt-1">{config.description}</p>
      </div>
      <div className="ml-4 flex items-center gap-3">
        {isEditing ? (
          <>
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-32 px-2 py-1 text-sm bg-transparent border-b border-blue-500 focus:outline-none text-right"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {saving ? '保存中...' : '保存'}
            </button>
            <button
              onClick={handleCancel}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              取消
            </button>
          </>
        ) : (
          <>
            <span className="text-sm text-gray-700">{config.value}</span>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              编辑
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const TokenManagement: React.FC<TokenManagementProps> = ({ currentUser }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'balance' | 'usage' | 'records' | 'config' | 'recharge'>('balance');
  const [loading, setLoading] = useState(false);

  // 检查 URL 参数，自动打开充值面板
  useEffect(() => {
    const openRecharge = searchParams.get('openRecharge');
    if (openRecharge === 'true') {
      // 切换到余额预览 Tab
      setActiveTab('balance');
      // 延迟打开购买弹框并加载套餐
      setTimeout(() => {
        openPurchaseDialog();
      }, 300);
      // 清除 URL 参数
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // 余额信息
  const [balance, setBalance] = useState({
    total_recharged: 0,
    used: 0,
    remaining: 0,
    is_overdraft: false,
    overdraft_limit: 0,
    usage_percent: 0,
    warning_threshold: 10,
    need_warning: false,
    status: 'active',
    total_prompt_tokens: 0,      // 新增：总输入Token
    total_completion_tokens: 0   // 新增：总输出Token
  });

  // 用量统计
  const [usageLogs, setUsageLogs] = useState<any[]>([]);
  const [usageByAction, setUsageByAction] = useState<any[]>([]);
  const [usageByModel, setUsageByModel] = useState<any[]>([]);

  // 图表数据
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [timeGranularity, setTimeGranularity] = useState<'hour' | 'day' | 'week' | 'month'>('day');
  const [selectedDate, setSelectedDate] = useState<string>(''); // 小时维度选择的日期
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  const [currentWeek, setCurrentWeek] = useState<number>(0); // 当前周数
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear()); // 当前年份

  // 获取某个日期是第几周
  const getWeekNumber = (date: Date): number => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  // 根据年份和周数获取日期范围
  const getWeekDateRange = (year: number, week: number) => {
    const firstDayOfYear = new Date(year, 0, 1);
    const daysOffset = (week - 1) * 7 - firstDayOfYear.getDay();
    const startDate = new Date(year, 0, 1 + daysOffset);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  // 切换周
  const changeWeek = (delta: number) => {
    const newWeek = currentWeek + delta;
    if (newWeek < 1 || newWeek > 53) return;
    setCurrentWeek(newWeek);
    const range = getWeekDateRange(currentYear, newWeek);
    setDateRange(range);
  };

  // 切换年
  const changeYear = (delta: number) => {
    const newYear = currentYear + delta;
    setCurrentYear(newYear);
    const firstDay = new Date(newYear, 0, 1);
    const lastDay = new Date(newYear, 11, 31);
    setDateRange({
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    });
  };

  // 充值记录
  const [rechargeRecords, setRechargeRecords] = useState<any[]>([]);

  // 系统配置（管理员）
  const [systemConfigs, setSystemConfigs] = useState<any[]>([]);

  // 充值套餐管理（超级管理员）
  const [editingPackage, setEditingPackage] = useState<any>(null);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: '',
    price: '',
    tokens: '',
    description: '',
    is_active: true,
    sort_order: 0
  });

  // 系统充值对话框
  const [showRechargeDialog, setShowRechargeDialog] = useState(false);
  const [systemRechargeMode, setSystemRechargeMode] = useState<'package' | 'custom'>('package');
  const [systemRechargePackage, setSystemRechargePackage] = useState<any>(null);
  const [systemRechargeTokens, setSystemRechargeTokens] = useState<string>('');
  const [systemRechargeAmount, setSystemRechargeAmount] = useState<string>('');
  const [systemRechargeRemark, setSystemRechargeRemark] = useState('');
  const [exchangeRate, setExchangeRate] = useState<number>(10000); // 默认1元=10000Token

  // 充值套餐购买弹框
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [rechargePackages, setRechargePackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [paymentQrCode, setPaymentQrCode] = useState<string>('');
  const [currentOrderNo, setCurrentOrderNo] = useState<string>('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const websocketRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 弹框打开时加载套餐（如果还没有加载）
  useEffect(() => {
    if (showPurchaseDialog && rechargePackages.length === 0) {
      loadRechargePackagesAndReturn().then(packages => {
        if (packages && packages.length > 0 && !selectedPackage) {
          handleSelectPackage(packages[0]);
        }
      });
    }
  }, [showPurchaseDialog]);

  // 组件卸载时清理轮询和WebSocket
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
    };
  }, []);
  
  // Toast
  const [toast, setToast] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });

  const isSuper = currentUser.role === 'super';
  const isAdmin = currentUser.role === 'super' || currentUser.role === 'admin'; // admin 及以上可查看管理功能

  // 初始加载
  useEffect(() => {
    loadAllData();
  }, []);

  // Tab切换时加载对应数据
  useEffect(() => {
    if (activeTab === 'usage') {
      loadUsageStats();
    } else if (activeTab === 'records') {
      loadRechargeRecords();
    } else if (activeTab === 'config' && isSuper) {
      loadSystemConfigs();
    } else if (activeTab === 'recharge' && isSuper) {
      loadRechargePackages();
    }
  }, [activeTab]);

  // 时间粒度或日期切换时重新加载图表数据
  useEffect(() => {
    if (activeTab === 'usage') {
      loadChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeGranularity, selectedDate, dateRange, activeTab]);

  // 加载所有数据（刷新功能）
  const loadAllData = async () => {
    setLoading(true);
    try {
      await loadBalance();
      if (activeTab === 'usage') {
        await loadUsageStats();
      } else if (activeTab === 'records') {
        await loadRechargeRecords();
      } else if (activeTab === 'config' && isSuper) {
        await loadSystemConfigs();
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载余额
  const loadBalance = async () => {
    try {
      const response = await tokenApi.getBalance();
      setBalance({
        ...response.data,
        // 确保这两个字段有默认值
        total_prompt_tokens: response.data.total_prompt_tokens || 0,
        total_completion_tokens: response.data.total_completion_tokens || 0
      });
    } catch (error: any) {
      showToast('error', error.response?.data?.detail || '加载余额失败');
    }
  };

  // 加载用量统计
  const loadUsageStats = async () => {
    try {
      const [logsRes, actionRes, modelRes] = await Promise.all([
        tokenApi.getUsageLogs(30),
        tokenApi.getUsageByAction(30),
        tokenApi.getUsageByModel(30)
      ]);
      setUsageLogs(logsRes.data.logs || []);
      setUsageByAction(actionRes.data.stats || []);
      setUsageByModel(modelRes.data.stats || []);
      
      // 加载图表数据
      await loadChartData();
    } catch (error: any) {
      showToast('error', '加载用量统计失败');
    }
  };

  // 加载图表数据
  const loadChartData = async () => {
    try {
      const params: any = {};
      const now = new Date();
      
      if (timeGranularity === 'hour') {
        // 小时：选择的日期当天24小时
        if (selectedDate) {
          const date = new Date(selectedDate);
          params.start_date = selectedDate;
          const nextDay = new Date(date);
          nextDay.setDate(nextDay.getDate() + 1);
          params.end_date = nextDay.toISOString().split('T')[0];
        } else {
          // 默认今天
          const today = now.toISOString().split('T')[0];
          params.start_date = today;
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          params.end_date = tomorrow.toISOString().split('T')[0];
          setSelectedDate(today);
        }
        params.granularity = 'hour';
      } else if (timeGranularity === 'day') {
        // 日：选择的日期范围
        if (dateRange.start && dateRange.end) {
          params.start_date = dateRange.start;
          params.end_date = dateRange.end;
        } else {
          // 默认本月
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          params.start_date = firstDay.toISOString().split('T')[0];
          params.end_date = lastDay.toISOString().split('T')[0];
          setDateRange({
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
          });
        }
        params.granularity = 'day';
      } else if (timeGranularity === 'week') {
        // 周：最近7天（今天往前推7天）
        if (dateRange.start && dateRange.end) {
          params.start_date = dateRange.start;
          params.end_date = dateRange.end;
        } else {
          const endDate = new Date(now);
          const startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 6); // 包括今天共7天
          params.start_date = startDate.toISOString().split('T')[0];
          params.end_date = endDate.toISOString().split('T')[0];
          setDateRange({
            start: startDate.toISOString().split('T')[0],
            end: endDate.toISOString().split('T')[0]
          });
        }
        params.granularity = 'day';
      } else if (timeGranularity === 'month') {
        // 月：选择的日期范围（默认今年）
        if (dateRange.start && dateRange.end) {
          params.start_date = dateRange.start;
          params.end_date = dateRange.end;
        } else {
          const firstDay = new Date(now.getFullYear(), 0, 1);
          const lastDay = new Date(now.getFullYear(), 11, 31);
          params.start_date = firstDay.toISOString().split('T')[0];
          params.end_date = lastDay.toISOString().split('T')[0];
          setDateRange({
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
          });
        }
        params.granularity = 'month';
      }
      
      console.log('📊 图表查询参数:', params);
      const response = await tokenApi.getUsageTrend(params);
      console.log('📊 图表返回数据:', response.data);
      setChartData(response.data.data || []);
    } catch (error: any) {
      console.error('❌ 加载图表数据失败:', error);
      showToast('error', '加载图表数据失败');
    }
  };

  // 加载充值记录
  const loadRechargeRecords = async () => {
    try {
      const response = await tokenApi.getRechargeRecords(50);
      setRechargeRecords(response.data.records || []);
    } catch (error: any) {
      showToast('error', '加载充值记录失败');
    }
  };

  // 保存充值套餐
  const savePackage = async () => {
    if (!packageForm.name || !packageForm.price || !packageForm.tokens) {
      showToast('warning', '请填写完整信息');
      return;
    }
    
    try {
      const url = editingPackage 
        ? `/api/auth/recharge/packages/${editingPackage.id}`
        : '/api/auth/recharge/packages';
      const method = editingPackage ? 'PUT' : 'POST';
      
      const response = await fetch(
        (process.env.REACT_APP_API_URL || '') + url,
        {
          method,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...packageForm,
            price: parseFloat(packageForm.price),
            tokens: parseInt(packageForm.tokens),
            sort_order: parseInt(packageForm.sort_order as any) || 0
          })
        }
      );
      
      const data = await response.json();
      if (data.success) {
        showToast('success', editingPackage ? '修改成功' : '创建成功');
        setShowPackageDialog(false);
        setEditingPackage(null);
        setPackageForm({ name: '', price: '', tokens: '', description: '', is_active: true, sort_order: 0 });
        loadRechargePackages();
      } else {
        showToast('error', data.message || '保存失败');
      }
    } catch (error) {
      showToast('error', '保存失败');
    }
  };

  // 删除充值套餐
  const deletePackage = async (id: number) => {
    if (!window.confirm('确定要删除这个套餐吗？')) return;
    
    try {
      const response = await fetch(
        (process.env.REACT_APP_API_URL || '') + `/api/auth/recharge/packages/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      
      const data = await response.json();
      if (data.success) {
        showToast('success', '删除成功');
        loadRechargePackages();
      } else {
        showToast('error', data.message || '删除失败');
      }
    } catch (error) {
      showToast('error', '删除失败');
    }
  };

  // 打开编辑套餐弹框
  const openEditPackage = (pkg: any) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      price: pkg.price.toString(),
      tokens: pkg.tokens.toString(),
      description: pkg.description || '',
      is_active: pkg.is_active,
      sort_order: pkg.sort_order || 0
    });
    setShowPackageDialog(true);
  };

  // 打开新增套餐弹框
  const openAddPackage = () => {
    setEditingPackage(null);
    setPackageForm({ name: '', price: '', tokens: '', description: '', is_active: true, sort_order: 0 });
    setShowPackageDialog(true);
  };

  // 加载系统配置
  const loadSystemConfigs = async () => {
    try {
      const response = await tokenApi.getSystemConfigs();
      const configs = response.data.configs || [];
      setSystemConfigs(configs);
      
      // 从配置中加载兑换比例
      const rateConfig = configs.find((c: any) => c.key === 'exchange_rate');
      if (rateConfig) {
        const rateMatch = rateConfig.value.match(/(\d+)/);
        if (rateMatch) {
          setExchangeRate(parseInt(rateMatch[1]));
        }
      }
    } catch (error: any) {
      showToast('error', '加载系统配置失败');
    }
  };

  // 系统充值Token
  const handleSystemRecharge = async () => {
    let tokens = 0;
    
    if (systemRechargeMode === 'package') {
      if (!systemRechargePackage) {
        showToast('warning', '请选择充值套餐');
        return;
      }
      tokens = systemRechargePackage.tokens;
    } else {
      tokens = parseInt(systemRechargeTokens) || 0;
      if (tokens <= 0) {
        showToast('warning', '请输入有效的Token数量');
        return;
      }
    }

    try {
      setLoading(true);
      await tokenApi.recharge({
        user_id: currentUser.id,
        tokens_amount: tokens,
        payment_amount: parseFloat(systemRechargeAmount) || 0,
        exchange_rate: `1元=${exchangeRate}Token`,
        remark_type: systemRechargeMode === 'package' ? 'package' : 'custom',
        remark: systemRechargeRemark || undefined,  // 如果用户喆填备注则忽略（remark_type优先）
      });
      showToast('success', '充值成功');
      setShowRechargeDialog(false);
      // 重置表单
      setSystemRechargePackage(null);
      setSystemRechargeTokens('');
      setSystemRechargeAmount('');
      setSystemRechargeRemark('');
      await loadAllData();
    } catch (error: any) {
      showToast('error', error.response?.data?.detail || '充值失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理Token输入变化（自动计算金额）
  const handleTokensChange = (value: string) => {
    setSystemRechargeTokens(value);
    const tokens = parseInt(value) || 0;
    const amount = tokens / exchangeRate;
    setSystemRechargeAmount(amount.toFixed(2));
  };

  // 处理金额输入变化（自动计算Token）
  const handleAmountChange = (value: string) => {
    setSystemRechargeAmount(value);
    const amount = parseFloat(value) || 0;
    const tokens = Math.floor(amount * exchangeRate);
    setSystemRechargeTokens(tokens.toString());
  };

  // 加载充值套餐
  const loadRechargePackages = async () => {
    try {
      const response = await fetch(
        (process.env.REACT_APP_API_URL || '') + '/api/auth/recharge/packages',
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setRechargePackages(data.data);
      }
    } catch (error) {
      console.error('加载充值套餐失败', error);
    }
  };

  // 创建支付订单
  const createPaymentOrder = async (packageId: number) => {
    setPaymentLoading(true);
    const token = localStorage.getItem('access_token');
    if (!token) {
      showToast('error', '登录已过期，请重新登录');
      setPaymentLoading(false);
      return;
    }
    
    try {
      const response = await fetch(
        (process.env.REACT_APP_API_URL || '') + `/api/auth/recharge/create-order?package_id=${packageId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 401) {
        showToast('error', '登录已过期，请重新登录');
        setPaymentLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setPaymentQrCode(data.data.code_url);
        setCurrentOrderNo(data.data.order_no);
        // 开始WebSocket监听
        startPaymentWebSocket(data.data.order_no);
      } else {
        showToast('error', data.message || '创建订单失败');
      }
    } catch (error) {
      showToast('error', '创建支付订单失败');
    } finally {
      setPaymentLoading(false);
    }
  };

  // WebSocket监听支付结果
  const startPaymentWebSocket = (orderNo: string) => {
    // 根据当前页面协议自动选择 ws:// 或 wss://
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = (process.env.REACT_APP_WS_URL || `${wsProtocol}//${window.location.host}`) + `/ws/payment/${orderNo}`;
    
    if (websocketRef.current) {
      websocketRef.current.close();
    }
    
    try {
      websocketRef.current = new WebSocket(wsUrl);
      let isConnected = false;
      let hasError = false;
      
      // 连接超时处理（5秒）
      const connectionTimeout = setTimeout(() => {
        if (!isConnected && websocketRef.current && !hasError) {
          console.log('WebSocket连接超时，降级为轮询模式');
          websocketRef.current.close();
          startPaymentPolling(orderNo);
        }
      }, 5000);
      
      websocketRef.current.onopen = () => {
        isConnected = true;
        clearTimeout(connectionTimeout);
        console.log('WebSocket连接成功，订单:', orderNo);
      };
      
      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket收到消息:', data);
          if (data.status === 'paid') {
            showToast('success', '支付成功！Token已到账');
            setShowPurchaseDialog(false);
            setSelectedPackage(null);
            setPaymentQrCode('');
            setCurrentOrderNo('');
            loadAllData();
            if (websocketRef.current) {
              websocketRef.current.close();
            }
          }
        } catch (e) {
          console.error('WebSocket消息解析失败:', e);
        }
      };
      
      websocketRef.current.onerror = (error) => {
        hasError = true;
        clearTimeout(connectionTimeout);
        if (!isConnected) {
          console.log('WebSocket连接失败，降级为轮询模式');
          // 降级为轮询模式
          startPaymentPolling(orderNo);
        }
      };
      
      websocketRef.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        if (!isConnected && !hasError) {
          // 连接未成功就关闭，启动轮询
          console.log('WebSocket连接关闭，降级为轮询模式');
          startPaymentPolling(orderNo);
        }
      };
    } catch (error) {
      console.log('WebSocket创建失败，使用轮询模式:', error);
      startPaymentPolling(orderNo);
    }
  };

  // 轮询检查支付状态
  const startPaymentPolling = (orderNo: string) => {
    // 清理之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setPaymentChecking(true);
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          (process.env.REACT_APP_API_URL || '') + `/api/auth/recharge/order-status?order_no=${orderNo}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );
        const data = await response.json();
        if (data.success && data.data.status === 'paid') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setPaymentChecking(false);
          showToast('success', '支付成功！Token已到账');
          setShowPurchaseDialog(false);
          setSelectedPackage(null);
          setPaymentQrCode('');
          setCurrentOrderNo('');
          loadAllData();
        }
      } catch (error) {
        console.error('检查支付状态失败', error);
      }
    }, 3000);

    // 5分钟后停止轮询
    setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPaymentChecking(false);
    }, 5 * 60 * 1000);
  };

  // 打开充值弹框
  const openPurchaseDialog = async () => {
    setShowPurchaseDialog(true);
    // 加载套餐并默认选中第一个
    const packages = await loadRechargePackagesAndReturn();
    if (packages && packages.length > 0) {
      handleSelectPackage(packages[0]);
    }
  };

  // 加载充值套餐并返回数据
  const loadRechargePackagesAndReturn = async (): Promise<any[]> => {
    try {
      const response = await fetch(
        (process.env.REACT_APP_API_URL || '') + '/api/auth/recharge/packages',
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setRechargePackages(data.data);
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('加载充值套餐失败', error);
      return [];
    }
  };

  // 选择套餐
  const handleSelectPackage = (pkg: any) => {
    setSelectedPackage(pkg);
    createPaymentOrder(pkg.id);
  };

  // 关闭充值弹框
  const closePurchaseDialog = () => {
    setShowPurchaseDialog(false);
    setSelectedPackage(null);
    setPaymentQrCode('');
    setCurrentOrderNo('');
    // 关闭WebSocket
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    // 停止轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPaymentChecking(false);
  };

  const showToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setToast({ show: true, type, message });
  };

  // 格式化数字
  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  // 翻译操作类型（将英文action翻译成中文）
  const translateAction = (action: string) => {
    const actionMap: { [key: string]: string } = {
      'chat_completion': 'AI提取',
      'stream_chat_completion': 'AI生成',
      'outline_generate': '目录生成',
      'content_generate': '内容生成',
      'document_analysis': '文档解析',
      'chapter_generate': '章节生成',
      'expand_analysis': '方案扩展分析',
      'table_extract': '表格提取',
      'mermaid_generate': '流程图生成',
      'check_result': '标书检查',
      'business_license_recognition': '多模态识别',
    };
    
    // 如果包含"多模态识别"关键词，直接返回（支持带后缀的情况）
    if (action.includes('多模态识别')) {
      return action;
    }
    
    // 如果包含"AI提取"关键词，直接返回（支持带后缀的情况，如"AI提取-表格生成"）
    if (action.includes('AI提取')) {
      return action;
    }
    
    // 如果包含"AI生成"关键词，直接返回（支持带后缀的情况）
    if (action.includes('AI生成')) {
      return action;
    }
    
    return actionMap[action] || action;
  };

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* 头部 */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Token管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理您的Token余额和使用情况</p>
          </div>
          <div className="flex items-center gap-3">
            {/* 刷新按钮 - 所有角色可见 */}
            <button
              onClick={loadAllData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              title="刷新数据"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </button>
            
            {/* 充值按钮 - 所有角色可见 */}
            <button
              onClick={openPurchaseDialog}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium"
            >
              <PlusIcon className="w-5 h-5" />
              充值
            </button>
            
            {/* 系统充值按钮（仅超级管理员） */}
            {isSuper && (
              <button
                onClick={async () => {
                  await loadRechargePackages();
                  setShowRechargeDialog(true);
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium"
              >
                <CurrencyDollarIcon className="w-5 h-5" />
                系统充值
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab导航 */}
      <div className="bg-white px-6">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('balance')}
            className={`py-4 border-b-2 transition-colors ${
              activeTab === 'balance'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5" />
              余额概览
            </div>
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`py-4 border-b-2 transition-colors ${
              activeTab === 'usage'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5" />
              用量统计
            </div>
          </button>
          <button
            onClick={() => setActiveTab('records')}
            className={`py-4 border-b-2 transition-colors ${
              activeTab === 'records'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5" />
              充值记录
            </div>
          </button>
          {isSuper && (
            <>
              <button
                onClick={() => setActiveTab('recharge')}
                className={`py-4 border-b-2 transition-colors ${
                  activeTab === 'recharge'
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CurrencyDollarIcon className="w-5 h-5" />
                  充值管理
                </div>
              </button>
              <button
                onClick={() => setActiveTab('config')}
                className={`py-4 border-b-2 transition-colors ${
                  activeTab === 'config'
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Cog6ToothIcon className="w-5 h-5" />
                  系统配置
                </div>
              </button>
            </>
          )}
        </div>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 内容区域 */}
      <div className="flex-1 overflow-auto p-6">
        {/* 加载状态 */}
        {loading && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        )}

        {/* 余额概览 */}
        {!loading && activeTab === 'balance' && (
          <div className="space-y-6">
            {/* 余额卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 累计充值 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">累计充值</h3>
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(balance.total_recharged)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Token</p>
              </div>

              {/* 剩余Token */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">剩余Token</h3>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    balance.is_overdraft ? 'bg-red-50' : 'bg-green-50'
                  }`}>
                    <CurrencyDollarIcon className={`w-6 h-6 ${
                      balance.is_overdraft ? 'text-red-600' : 'text-green-600'
                    }`} />
                  </div>
                </div>
                <p className={`text-3xl font-bold ${
                  balance.is_overdraft ? 'text-red-600' : 'text-gray-900'
                }`}>
                  {formatNumber(balance.remaining)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {balance.is_overdraft ? '欠费状态' : 'Token'}
                </p>
              </div>

              {/* 已使用 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">已使用</h3>
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <ChartBarIcon className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(balance.used)}
                </p>
                {/* 输入/输出Token统计 */}
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">输入Token</span>
                    <span className="font-medium text-blue-600">
                      {formatNumber(balance.total_prompt_tokens)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">输出Token</span>
                    <span className="font-medium text-green-600">
                      {formatNumber(balance.total_completion_tokens)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 使用进度条 */}
            {balance.total_recharged > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">使用情况</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      已使用 {formatNumber(balance.used)} / 
                      累计 {formatNumber(balance.total_recharged)} Token
                    </span>
                    <span className={`font-medium ${
                      balance.need_warning ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {balance.usage_percent.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        balance.need_warning 
                          ? 'bg-yellow-500'
                          : (100 - balance.usage_percent) < 30
                            ? 'bg-orange-500' 
                            : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100 - balance.usage_percent, 100)}%` }}
                    />
                  </div>
                  {balance.need_warning && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ Token余额不足{balance.warning_threshold}%，请及时充值
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* 账户信息 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">账户信息</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">账户状态</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {balance.status === 'active' ? '正常' : '异常'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {balance.is_overdraft ? '已欠费金额' : '可用余额'}
                  </p>
                  <p className={`text-sm font-medium mt-1 ${
                    balance.is_overdraft ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {balance.is_overdraft 
                      ? formatNumber(Math.abs(balance.remaining)) 
                      : formatNumber(balance.remaining)} Token
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">预警阈值</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {balance.warning_threshold}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {balance.is_overdraft ? '最大可欠额度' : '欠费保护额度'}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatNumber(balance.overdraft_limit)} Token
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 用量统计 */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            {/* Token使用趋势图表 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Token使用趋势</h3>
                
                {/* 中间：时间选择器 */}
                <div className="flex items-center gap-3">
                  {timeGranularity === 'hour' ? (
                    // 小时维度：选择日期
                    <>
                      <span className="text-sm text-gray-600 font-medium">选择日期：</span>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </>
                  ) : timeGranularity === 'day' ? (
                    // 日维度：选择日期范围
                    <>
                      <span className="text-sm text-gray-600 font-medium">日期范围：</span>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <span className="text-sm text-gray-400">至</span>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </>
                  ) : timeGranularity === 'week' ? (
                    // 周维度：左右切换周
                    <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                      <button
                        onClick={() => changeWeek(-1)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="上一周"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm font-medium text-gray-800 min-w-[140px] text-center">
                        {currentYear}年第{currentWeek}周
                      </span>
                      <button
                        onClick={() => changeWeek(1)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="下一周"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    // 月维度：左右切换年
                    <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                      <button
                        onClick={() => changeYear(-1)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="上一年"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm font-medium text-gray-800 min-w-[100px] text-center">
                        {currentYear}年
                      </span>
                      <button
                        onClick={() => changeYear(1)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="下一年"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* 右侧：图表类型和时间粒度 */}
                <div className="flex items-center gap-3">
                  {/* 图表类型切换 */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setChartType('line')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        chartType === 'line'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      曲线图
                    </button>
                    <button
                      onClick={() => setChartType('bar')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        chartType === 'bar'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      柱状图
                    </button>
                  </div>

                  {/* 时间粒度选择 */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => {
                        setTimeGranularity('hour');
                        setSelectedDate(new Date().toISOString().split('T')[0]);
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeGranularity === 'hour'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      小时
                    </button>
                    <button
                      onClick={() => {
                        setTimeGranularity('day');
                        const now = new Date();
                        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                        setDateRange({
                          start: firstDay.toISOString().split('T')[0],
                          end: lastDay.toISOString().split('T')[0]
                        });
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeGranularity === 'day'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      日
                    </button>
                    <button
                      onClick={() => {
                        setTimeGranularity('week');
                        const now = new Date();
                        const week = getWeekNumber(now);
                        const year = now.getFullYear();
                        setCurrentWeek(week);
                        setCurrentYear(year);
                        const endDate = new Date(now);
                        const startDate = new Date(now);
                        startDate.setDate(startDate.getDate() - 6);
                        setDateRange({
                          start: startDate.toISOString().split('T')[0],
                          end: endDate.toISOString().split('T')[0]
                        });
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeGranularity === 'week'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      周
                    </button>
                    <button
                      onClick={() => {
                        setTimeGranularity('month');
                        const now = new Date();
                        const year = now.getFullYear();
                        setCurrentYear(year);
                        const firstDay = new Date(year, 0, 1);
                        const lastDay = new Date(year, 11, 31);
                        setDateRange({
                          start: firstDay.toISOString().split('T')[0],
                          end: lastDay.toISOString().split('T')[0]
                        });
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        timeGranularity === 'month'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      月
                    </button>
                  </div>
                </div>
              </div>

              {/* 图表容器 */}
              <div className="w-full" style={{ height: '320px', minHeight: '320px' }}>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    {chartType === 'line' ? (
                      <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="time"
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                          tickFormatter={(value) => {
                            // 根据粒度格式化X轴标签
                            if (timeGranularity === 'hour') {
                              // 小时：只显示 HH:00
                              return value.split(' ')[1] || value;
                            } else if (timeGranularity === 'day' || timeGranularity === 'week') {
                              // 日/周：只显示 MM-DD
                              const parts = value.split('-');
                              if (parts.length === 3) {
                                return `${parts[1]}-${parts[2]}`;
                              }
                              return value;
                            } else if (timeGranularity === 'month') {
                              // 月：只显示月份
                              const parts = value.split('-');
                              if (parts.length === 2) {
                                return `${parts[1]}月`;
                              }
                              return value;
                            }
                            return value;
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                          label={{ value: 'Token数量', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'tokens') return [formatNumber(value), 'Token消耗'];
                            if (name === 'count') return [value, '调用次数'];
                            return [value, name || ''];
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: '12px' }}
                          formatter={(value) => {
                            if (value === 'tokens') return 'Token消耗';
                            if (value === 'count') return '调用次数';
                            return value;
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="tokens"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    ) : (
                      <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="time"
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                          tickFormatter={(value) => {
                            // 根据粒度格式化X轴标签
                            if (timeGranularity === 'hour') {
                              // 小时：只显示 HH:00
                              return value.split(' ')[1] || value;
                            } else if (timeGranularity === 'day' || timeGranularity === 'week') {
                              // 日/周：只显示 MM-DD
                              const parts = value.split('-');
                              if (parts.length === 3) {
                                return `${parts[1]}-${parts[2]}`;
                              }
                              return value;
                            } else if (timeGranularity === 'month') {
                              // 月：只显示月份
                              const parts = value.split('-');
                              if (parts.length === 2) {
                                return `${parts[1]}月`;
                              }
                              return value;
                            }
                            return value;
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                          label={{ value: 'Token数量', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                          formatter={(value: any, name?: string) => {
                            if (name === 'tokens') return [formatNumber(value), 'Token消耗'];
                            if (name === 'count') return [value, '调用次数'];
                            return [value, name || ''];
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: '12px' }}
                          formatter={(value) => {
                            if (value === 'tokens') return 'Token消耗';
                            if (value === 'count') return '调用次数';
                            return value;
                          }}
                        />
                        <Bar dataKey="tokens" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">暂无使用数据</p>
                  </div>
                )}
              </div>
            </div>

            {/* 按操作类型统计 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">按操作类型统计（近30天）</h3>
              {usageByAction.length > 0 ? (
                <div className="space-y-3">
                  {usageByAction.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{translateAction(item.action)}</p>
                        <p className="text-xs text-gray-500 mt-1">调用次数：{item.count}</p>
                      </div>
                      <p className="text-sm font-semibold text-blue-600">
                        {formatNumber(item.total_tokens)} Token
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">暂无使用记录</p>
              )}
            </div>

            {/* 按模型统计 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">按模型统计（近30天）</h3>
              {usageByModel.length > 0 ? (
                <div className="space-y-3">
                  {usageByModel.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.model_name}</p>
                        <p className="text-xs text-gray-500 mt-1">调用次数：{item.count}</p>
                      </div>
                      <p className="text-sm font-semibold text-purple-600">
                        {formatNumber(item.total_tokens)} Token
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">暂无使用记录</p>
              )}
            </div>

            {/* 最近使用记录 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">最近使用记录</h3>
              {usageLogs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">操作类型</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">模型</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">消耗Token</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">时间</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {usageLogs.slice(0, 20).map((log, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">
                            {log.operation_label || translateAction(log.action)}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {log.model_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 font-medium">
                            {formatNumber(log.tokens_used)}
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            {formatDate(log.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">暂无使用记录</p>
              )}
            </div>
          </div>
        )}

        {/* 充值记录 */}
        {activeTab === 'records' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">充值记录</h3>
            </div>
            <div className="overflow-x-auto">
              {rechargeRecords.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">订单号</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">充值Token</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">金额</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">类型</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">时间</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">备注</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rechargeRecords.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-900 font-mono text-xs">
                          {record.order_no}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-900 font-semibold">
                          +{formatNumber(record.tokens_amount)}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-600">
                          {record.payment_amount ? `¥${record.payment_amount}` : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            record.recharge_type === 'gift' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {record.recharge_type === 'gift' ? '赠送' : '充值'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600 text-xs">
                          {formatDate(record.created_at)}
                        </td>
                        <td className="px-6 py-4 text-gray-600 text-xs">
                          {record.remark || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500 text-center py-12">暂无充值记录</p>
              )}
            </div>
          </div>
        )}

        {/* 充值管理（仅超级管理员） */}
        {activeTab === 'recharge' && isSuper && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">充值套餐管理</h3>
              <button
                onClick={openAddPackage}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <PlusIcon className="w-4 h-4" />
                新增套餐
              </button>
            </div>
            <div className="overflow-x-auto">
              {rechargePackages.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">排序</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">套餐名称</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">价格</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Token数</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">描述</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">状态</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rechargePackages.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-900">{pkg.sort_order}</td>
                        <td className="px-6 py-4 text-gray-900 font-medium">{pkg.name}</td>
                        <td className="px-6 py-4 text-right text-green-600 font-semibold">¥{pkg.price}</td>
                        <td className="px-6 py-4 text-right text-gray-900">{pkg.tokens.toLocaleString()}</td>
                        <td className="px-6 py-4 text-gray-600 max-w-xs truncate">{pkg.description || '-'}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            pkg.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {pkg.is_active ? '启用' : '禁用'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openEditPackage(pkg)}
                              className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-xs"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => deletePackage(pkg.id)}
                              className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500 text-center py-12">暂无充值套餐</p>
              )}
            </div>
          </div>
        )}

        {/* 系统配置（仅超级管理员） */}
        {activeTab === 'config' && isSuper && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">系统配置</h3>
            </div>
            <div className="p-6">
              {systemConfigs.length > 0 ? (
                <div className="space-y-4">
                  {systemConfigs.map((config, index) => (
                    <SystemConfigItem
                      key={config.key}
                      config={config}
                      onSave={async (newValue) => {
                        try {
                          const response = await fetch(
                            (process.env.REACT_APP_API_URL || '') + `/api/token/config/${encodeURIComponent(config.key)}?value=${encodeURIComponent(newValue)}`,
                            {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                              }
                            }
                          );
                          if (response.ok) {
                            showToast('success', '保存成功');
                            const newConfigs = [...systemConfigs];
                            newConfigs[index].value = newValue;
                            setSystemConfigs(newConfigs);
                            return true;
                          } else {
                            showToast('error', '保存失败');
                            return false;
                          }
                        } catch (error) {
                          showToast('error', '保存失败');
                          return false;
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">暂无配置信息</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 系统充值对话框 */}
      {showRechargeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">系统充值Token</h3>
            </div>
            <div className="p-6">
              {/* 模式切换 */}
              <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setSystemRechargeMode('package')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                    systemRechargeMode === 'package'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  选择套餐
                </button>
                <button
                  onClick={() => setSystemRechargeMode('custom')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                    systemRechargeMode === 'custom'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  自定义充值
                </button>
              </div>

              {/* 套餐模式 */}
              {systemRechargeMode === 'package' && (
                <div className="space-y-3 mb-6">
                  {rechargePackages.filter(p => p.is_active).map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => {
                        setSystemRechargePackage(pkg);
                        setSystemRechargeTokens(pkg.tokens.toString());
                        setSystemRechargeAmount(pkg.price.toString());
                      }}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        systemRechargePackage?.id === pkg.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900">{pkg.name}</h4>
                          <p className="text-xs text-gray-500 mt-1">{pkg.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-blue-600">¥{pkg.price}</div>
                          <div className="text-xs text-gray-500">{pkg.tokens.toLocaleString()} Token</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 自定义模式 */}
              {systemRechargeMode === 'custom' && (
                <div className="space-y-4 mb-6">
                  <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-4">
                    兑换比例：1元 = {exchangeRate.toLocaleString()} Token
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Token数量
                    </label>
                    <input
                      type="number"
                      value={systemRechargeTokens}
                      onChange={(e) => handleTokensChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="请输入Token数量"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      支付金额（元）
                    </label>
                    <input
                      type="number"
                      value={systemRechargeAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="请输入支付金额"
                      step="0.01"
                    />
                  </div>
                </div>
              )}

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  备注
                </label>
                <textarea
                  value={systemRechargeRemark}
                  onChange={(e) => setSystemRechargeRemark(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="可选"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowRechargeDialog(false);
                  setSystemRechargePackage(null);
                  setSystemRechargeTokens('');
                  setSystemRechargeAmount('');
                  setSystemRechargeRemark('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSystemRecharge}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {loading ? '充值中...' : '确认充值'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.message}
        duration={3000}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* 套餐编辑/新增弹框 */}
      {showPackageDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingPackage ? '编辑套餐' : '新增套餐'}
              </h3>
              <button
                onClick={() => setShowPackageDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">套餐名称</label>
                <input
                  type="text"
                  value={packageForm.name}
                  onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="如：基础套餐"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">价格（元）</label>
                  <input
                    type="number"
                    value={packageForm.price}
                    onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="10.00"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Token数量</label>
                  <input
                    type="number"
                    value={packageForm.tokens}
                    onChange={(e) => setPackageForm({ ...packageForm, tokens: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1000000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
                <input
                  type="text"
                  value={packageForm.description}
                  onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="套餐描述"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">排序</label>
                  <input
                    type="number"
                    value={packageForm.sort_order}
                    onChange={(e) => setPackageForm({ ...packageForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={packageForm.is_active}
                      onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">启用</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowPackageDialog(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={savePackage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingPackage ? '保存' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 充值套餐购买弹框 - 左右布局 */}
      {showPurchaseDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Token充值</h3>
              <button
                onClick={closePurchaseDialog}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="flex flex-col md:flex-row">
              {/* 左侧：套餐选择 */}
              <div className="w-full md:w-1/2 p-6 border-b md:border-b-0 md:border-r border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-4">选择充值套餐</h4>
                {rechargePackages.length > 0 ? (
                  <div className="space-y-3">
                    {rechargePackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => handleSelectPackage(pkg)}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedPackage?.id === pkg.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-400 hover:bg-green-50/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">{pkg.name}</h4>
                            <p className="text-xs text-gray-500 mt-1">{pkg.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-green-600">¥{pkg.price}</div>
                            <div className="text-xs text-gray-500">{pkg.tokens.toLocaleString()} Token</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                      <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                    <p className="text-gray-500 text-sm">加载套餐中...</p>
                  </div>
                )}
              </div>
              
              {/* 右侧：支付二维码 */}
              <div className="w-full md:w-1/2 p-6 flex flex-col items-center justify-center bg-gray-50/50">
                {selectedPackage ? (
                  <>
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-600">应付金额</p>
                      <p className="text-3xl font-bold text-green-600">¥{selectedPackage.price}</p>
                      <p className="text-xs text-gray-500 mt-1">{selectedPackage.tokens.toLocaleString()} Token</p>
                    </div>
                    
                    {paymentLoading ? (
                      <div className="py-8 text-center">
                        <ArrowPathIcon className="w-10 h-10 mx-auto text-gray-400 animate-spin mb-3" />
                        <p className="text-gray-500 text-sm">正在生成支付二维码...</p>
                      </div>
                    ) : paymentQrCode ? (
                      <div className="text-center">
                        <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 inline-block">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(paymentQrCode)}`}
                            alt="支付二维码"
                            className="w-44 h-44"
                          />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/>
                          </svg>
                          <p className="text-sm text-gray-600">微信扫一扫支付</p>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          支付完成后将自动到账
                        </p>
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 rounded-lg flex items-center justify-center">
                          <QrCodeIcon className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-sm text-red-500">生成二维码失败</p>
                        <button
                          onClick={() => handleSelectPackage(selectedPackage)}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                        >
                          点击重试
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 rounded-lg flex items-center justify-center">
                      <QrCodeIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">请选择左侧套餐</p>
                    <p className="text-xs text-gray-400 mt-1">选择后将生成支付二维码</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagement;
